<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, EffectCoverflow, Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/effect-coverflow'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import type { AddonCard } from '@/types/types'

const props = defineProps<{
  addonDetail: AddonCard
}>()

const { t } = useI18n()
const { openInNewTab } = useDOM()
const { sendEvent } = useAddon()
const { addonsEventsRecorder } = useLocalStorage()

const emit = defineEmits<{ (e: 'onClick', id: number, buttonSlug: string): void }>()

const handleClick = (id: number) => {
  const content = props.addonDetail.content
  const button = content.actionButtons[id]

  addonsEventsRecorder.value.push(`${props.addonDetail.slug}-${content.actionButtons[id].slug}`)

  if (content.actionButtons[id].link) {
    openInNewTab(button.link)
    sendEvent(props.addonDetail.id, 'addons:detail:clicked', button.slug)
    return
  }

  emit('onClick', button.confirmationModalId, button.slug)
}

const isBtnAlreadyClicked = (id: number) => {
  return addonsEventsRecorder.value.includes(
    `${props.addonDetail.slug}-${props.addonDetail.content.actionButtons[id]?.slug}`
  )
}

const btnText = (id: number) => {
  return isBtnAlreadyClicked(id) ? t('pages.addons.sent') : props.addonDetail.content.actionButtons[id].text
}
</script>

<template>
  <div class="flex h-full min-h-screen flex-col text-left text-foreground md:h-auto md:min-h-0 md:justify-between">
    <div class="flex flex-col items-start justify-between gap-1 p-4 sm:ml-4 sm:flex-col sm:pl-6">
      <p class="text-lg font-semibold leading-5 text-primary">
        <span v-if="addonDetail.content.label" v-html="addonDetail.content.label" />
      </p>
    </div>
    <div class="flex h-full flex-col justify-between">
      <div class="p-4 pb-20 md:max-h-[60vh] md:overflow-y-auto md:pb-0">
        <div class="text-sm sm:mb-2 sm:px-6">
          <p v-html="addonDetail.content.description" />
        </div>
        <div class="flex-col items-start overflow-y-auto break-words p-4 px-0 pb-4 text-left sm:px-6">
          <div class="flex flex-row space-x-2 text-xs font-light text-primary">
            <p class="text-lg font-semibold leading-5"></p>
          </div>
          <Swiper
            class="pb-5"
            effect="fade"
            :loop="true"
            :autoplay="{
              disableOnInteraction: false,
              delay: 50500
            }"
            :space-between="10"
            :slides-per-view="1.1"
            :pagination="{
              clickable: true
            }"
            :navigation="true"
            :modules="[Autoplay, EffectCoverflow, Navigation, Pagination]"
            :breakpoints="{
              640: {
                spaceBetween: 20
              }
            }"
          >
            <SwiperSlide v-for="(image, index) in addonDetail.content.images" :key="index">
              <img :src="image" class="h-96 w-full cursor-pointer object-cover md:rounded-md" />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
      <div class="fixed bottom-0 z-10 mt-2 w-full rounded-b-md border-t bg-white p-3 md:relative">
        <div :class="'mx-auto flex w-full items-center justify-center md:justify-end gap-2'">
          <Button
            :disabled="isBtnAlreadyClicked(0)"
            :variant="
              addonDetail.content.actionButtons.length > 1 && !isBtnAlreadyClicked(0) ? 'outline' : 'default-primary'
            "
            @click="handleClick(0)"
            >{{ btnText(0) }}</Button
          >
          <Button
            v-if="addonDetail.content.actionButtons[1]"
            :disabled="isBtnAlreadyClicked(1)"
            variant="default-primary"
            @click="handleClick(1)"
            >{{ btnText(1) }}</Button
          >
        </div>
      </div>
    </div>
  </div>
</template>
